import { useState, useEffect, useCallback } from "react";
import { ConfirmModal } from "@/components/ConfirmModal";
import {
  Plus, Pencil, Trash2, Search, Eye, EyeOff, Star, StarOff, Copy, GripVertical,
  ChevronDown, Upload, X, ExternalLink, Check, Loader2, Image as ImageIcon, ArrowUpDown,
  Wand2, RotateCcw, Save, Sparkles, Tv,
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";

const ICON_OPTIONS = [
  'tv', 'newspaper', 'clapperboard', 'church', 'trophy', 'music', 'baby',
  'graduation-cap', 'heart-pulse', 'briefcase', 'film', 'camera', 'laugh',
  'flask-conical', 'cpu', 'gamepad-2', 'utensils', 'plane', 'heart', 'shirt'
];

const COLOR_OPTIONS = [
  '#3B82F6', '#A855F7', '#F59E0B', '#22C55E', '#EC4899', '#F97316',
  '#06B6D4', '#E11D48', '#8B5CF6', '#DC2626', '#0EA5E9', '#FBBF24',
  '#14B8A6', '#6366F1', '#7C3AED', '#EA580C', '#0D9488', '#EF4444',
  '#D946EF', '#64748B'
];

function generateSlug(name) {
  return name.toLowerCase().trim()
    .replace(/[àáâãäå]/g, 'a').replace(/[èéêë]/g, 'e').replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o').replace(/[ùúûü]/g, 'u').replace(/[ç]/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function CategoryForm({ category, onSave, onClose, allCategories }) {
  const isEditing = !!category;
  const [form, setForm] = useState({
    name_fr: '', name_en: '', slug: '', description_fr: '', description_en: '',
    image_url: '', image_alt_fr: '', image_alt_en: '', icon: 'tv', accent_color: '#64748B',
    display_order: 99, is_active: 1, is_featured: 0,
    seo_title_fr: '', seo_title_en: '', seo_description_fr: '', seo_description_en: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [showSeo, setShowSeo] = useState(false);

  useEffect(() => {
    if (category) {
      setForm({
        name_fr: category.name_fr || category.name || '',
        name_en: category.name_en || category.name || '',
        slug: category.slug || '',
        description_fr: category.description_fr || '',
        description_en: category.description_en || '',
        image_url: category.image_url || '',
        image_alt_fr: category.image_alt_fr || '',
        image_alt_en: category.image_alt_en || '',
        icon: category.icon || 'tv',
        accent_color: category.accent_color || '#64748B',
        display_order: category.display_order ?? 99,
        is_active: category.is_active ?? 1,
        is_featured: category.is_featured ?? 0,
        seo_title_fr: category.seo_title_fr || '',
        seo_title_en: category.seo_title_en || '',
        seo_description_fr: category.seo_description_fr || '',
        seo_description_en: category.seo_description_en || '',
      });
      setSlugEdited(true);
    }
  }, [category]);

  const updateField = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'name_fr' && !slugEdited) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const catId = category?.id || 'temp';
      const res = await axios.post(`${API}/categories/${catId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      updateField('image_url', res.data.image_url);
    } catch { /* upload failed */ }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.name_fr.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, name: form.name_fr };
      if (isEditing) {
        await axios.put(`${API}/categories/${category.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/categories`, payload, { withCredentials: true });
      }
      onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error saving category');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-10 bg-slate-950/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl border border-slate-800 my-4" data-testid="category-form-modal">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/50 sticky top-0 rounded-t-2xl z-10">
          <h3 className="font-bold text-lg text-white">{isEditing ? 'Edit Category' : 'Add Category'}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors" data-testid="form-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Names */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-400 mb-1 block">French Name *</label>
              <input
                type="text" data-testid="cat-name-fr"
                className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.name_fr} onChange={(e) => updateField('name_fr', e.target.value)}
                placeholder="Actualités"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 mb-1 block">English Name</label>
              <input
                type="text" data-testid="cat-name-en"
                className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.name_en} onChange={(e) => updateField('name_en', e.target.value)}
                placeholder="News"
              />
            </div>
          </div>

          {/* Slug */}
          <div>
            <label className="text-xs font-bold text-slate-400 mb-1 block">Slug</label>
            <input
              type="text" data-testid="cat-slug"
              className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
              value={form.slug}
              onChange={(e) => { setSlugEdited(true); updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); }}
              placeholder="news"
            />
          </div>

          {/* Descriptions */}
          <div>
            <label className="text-xs font-bold text-slate-400 mb-1 block">French Description</label>
            <textarea
              data-testid="cat-desc-fr" rows={2}
              className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
              value={form.description_fr} onChange={(e) => updateField('description_fr', e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-400 mb-1 block">English Description</label>
            <textarea
              data-testid="cat-desc-en" rows={2}
              className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
              value={form.description_en} onChange={(e) => updateField('description_en', e.target.value)}
            />
          </div>

          {/* Image */}
          <div>
            <label className="text-xs font-bold text-slate-400 mb-1 block">Category Image</label>
            {form.image_url && (
              <div className="relative mb-2 rounded-lg overflow-hidden border border-slate-700">
                <img src={form.image_url} alt="" className="w-full h-32 object-cover" />
                <button onClick={() => updateField('image_url', '')} className="absolute top-2 right-2 p-1 rounded bg-red-500/80 text-white hover:bg-red-500">
                  <X size={14} />
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <label className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700 bg-slate-950 text-slate-400 text-sm cursor-pointer hover:border-blue-500 transition-colors">
                <Upload size={14} />
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input type="file" accept="image/webp,image/jpg,image/jpeg,image/png" className="hidden" onChange={handleImageUpload} disabled={uploading} />
              </label>
              <input
                type="text" placeholder="Or paste image URL..."
                className="flex-1 px-3 py-2 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.image_url} onChange={(e) => updateField('image_url', e.target.value)}
              />
            </div>
          </div>

          {/* Icon & Color */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-400 mb-1 block">Icon</label>
              <div className="grid grid-cols-5 gap-1.5">
                {ICON_OPTIONS.map(icon => (
                  <button
                    key={icon} type="button"
                    onClick={() => updateField('icon', icon)}
                    className={`p-1.5 rounded-lg text-[10px] border transition-all ${form.icon === icon ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-slate-700 text-slate-500 hover:border-slate-600'}`}
                    title={icon}
                  >
                    {icon.substring(0, 4)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 mb-1 block">Accent Color</label>
              <div className="grid grid-cols-5 gap-1.5">
                {COLOR_OPTIONS.map(color => (
                  <button
                    key={color} type="button"
                    onClick={() => updateField('accent_color', color)}
                    className={`w-full aspect-square rounded-lg border-2 transition-all ${form.accent_color === color ? 'border-white scale-110' : 'border-transparent hover:border-white/30'}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Order, Active, Featured */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-400 mb-1 block">Display Order</label>
              <input
                type="number" data-testid="cat-order"
                className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.display_order} onChange={(e) => updateField('display_order', parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 mb-1 block">Status</label>
              <button
                type="button"
                onClick={() => updateField('is_active', form.is_active === 1 ? 0 : 1)}
                className={`w-full px-3 py-2 rounded-lg text-sm font-medium border transition-all ${form.is_active === 1 ? 'border-green-600/30 bg-green-600/10 text-green-400' : 'border-slate-700 bg-slate-950 text-slate-500'}`}
              >
                {form.is_active === 1 ? 'Active' : 'Inactive'}
              </button>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 mb-1 block">Featured</label>
              <button
                type="button"
                onClick={() => updateField('is_featured', form.is_featured === 1 ? 0 : 1)}
                className={`w-full px-3 py-2 rounded-lg text-sm font-medium border transition-all ${form.is_featured === 1 ? 'border-yellow-600/30 bg-yellow-600/10 text-yellow-400' : 'border-slate-700 bg-slate-950 text-slate-500'}`}
              >
                {form.is_featured === 1 ? 'Featured' : 'Not Featured'}
              </button>
            </div>
          </div>

          {/* SEO Toggle */}
          <button
            type="button"
            onClick={() => setShowSeo(!showSeo)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ChevronDown size={14} className={`transition-transform ${showSeo ? 'rotate-180' : ''}`} />
            SEO Settings
          </button>
          {showSeo && (
            <div className="space-y-3 border border-slate-800 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">SEO Title (FR)</label>
                  <input type="text" className="w-full px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm" value={form.seo_title_fr} onChange={(e) => updateField('seo_title_fr', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">SEO Title (EN)</label>
                  <input type="text" className="w-full px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm" value={form.seo_title_en} onChange={(e) => updateField('seo_title_en', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">SEO Desc (FR)</label>
                  <textarea rows={2} className="w-full px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm resize-none" value={form.seo_description_fr} onChange={(e) => updateField('seo_description_fr', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">SEO Desc (EN)</label>
                  <textarea rows={2} className="w-full px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm resize-none" value={form.seo_description_en} onChange={(e) => updateField('seo_description_en', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Image Alt (FR)</label>
                  <input type="text" className="w-full px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm" value={form.image_alt_fr} onChange={(e) => updateField('image_alt_fr', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Image Alt (EN)</label>
                  <input type="text" className="w-full px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm" value={form.image_alt_en} onChange={(e) => updateField('image_alt_en', e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-800 flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-700 text-slate-400 font-medium hover:bg-slate-800 hover:text-white transition-all text-sm">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name_fr.trim()}
            data-testid="category-save-btn"
            className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500 transition-all disabled:opacity-50 text-sm flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: "", message: "", onConfirm: () => {} });
  const [bulkAction, setBulkAction] = useState("");
  const [aiModal, setAiModal] = useState(null);
  const [bulkGenModal, setBulkGenModal] = useState(false);
  const [channelsModal, setChannelsModal] = useState(null);
  const [channelsData, setChannelsData] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const itemsPerPage = 15;

  const fetchCategories = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/categories`, { withCredentials: true }).then(res => {
      setCategories(res.data.categories || res.data || []);
      setLoading(false);
    }).catch(() => {
      // Fallback to public endpoint
      axios.get(`${API}/categories`).then(res => {
        setCategories(res.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    });
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleDelete = (cat) => {
    const count = cat.channel_count || 0;
    setConfirmModal({
      isOpen: true,
      title: `Delete "${cat.name_fr || cat.name}"`,
      message: count > 0
        ? `This category has ${count} assigned channels. Channels will be unassigned but not deleted. Continue?`
        : 'Are you sure you want to delete this category? It can be restored later.',
      onConfirm: async () => {
        await axios.delete(`${API}/admin/categories/${cat.id}/soft`, {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
          data: { action: 'keep' },
        });
        fetchCategories();
        setSelectedIds(prev => prev.filter(id => id !== cat.id));
      }
    });
  };

  const handleRestore = async (cat) => {
    await axios.post(`${API}/admin/categories/${cat.id}/restore`, {}, { withCredentials: true });
    fetchCategories();
  };

  const viewChannels = async (cat) => {
    setChannelsModal(cat);
    setChannelsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/categories/${cat.id}/channels`, { withCredentials: true });
      setChannelsData(res.data.channels || []);
    } catch { setChannelsData([]); }
    setChannelsLoading(false);
  };

  const handleToggle = async (cat) => {
    await axios.patch(`${API}/categories/${cat.id}/toggle`, {}, { withCredentials: true });
    fetchCategories();
  };

  const handleToggleFeatured = async (cat) => {
    await axios.patch(`${API}/categories/${cat.id}/featured`, {}, { withCredentials: true });
    fetchCategories();
  };

  const handleDuplicate = async (cat) => {
    await axios.post(`${API}/categories/${cat.id}/duplicate`, {}, { withCredentials: true });
    fetchCategories();
  };

  const handleBulkAction = () => {
    if (!bulkAction || selectedIds.length === 0) return;
    if (bulkAction === 'delete') {
      setConfirmModal({
        isOpen: true,
        title: `Delete ${selectedIds.length} Categories`,
        message: `Channels in these categories will remain. Continue?`,
        onConfirm: async () => {
          await axios.post(`${API}/admin/categories/bulk`, { ids: selectedIds, action: 'delete' }, { withCredentials: true });
          setSelectedIds([]);
          setBulkAction("");
          fetchCategories();
        }
      });
    } else if (bulkAction === 'restore') {
      axios.post(`${API}/admin/categories/bulk`, { ids: selectedIds, action: 'restore' }, { withCredentials: true })
        .then(() => { setSelectedIds([]); setBulkAction(""); fetchCategories(); });
    } else {
      axios.post(`${API}/admin/categories/bulk`, { ids: selectedIds, action: bulkAction }, { withCredentials: true })
        .then(() => { setSelectedIds([]); setBulkAction(""); fetchCategories(); });
    }
  };

  // Compute channel counts from the full category data
  const [channelCounts, setChannelCounts] = useState({});
  useEffect(() => {
    axios.get(`${API}/categories/public?lang=en`).then(res => {
      const counts = {};
      res.data.forEach(c => { counts[c.id] = c.channel_count; });
      setChannelCounts(counts);
    }).catch(() => {});
  }, [categories]);

  // Filter
  let filtered = categories;
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.name_fr || '').toLowerCase().includes(q) ||
      (c.name_en || '').toLowerCase().includes(q) ||
      (c.slug || '').toLowerCase().includes(q)
    );
  }
  if (filterStatus === 'active') filtered = filtered.filter(c => c.is_active === 1 && !c.deleted_at);
  if (filterStatus === 'inactive') filtered = filtered.filter(c => c.is_active !== 1 && !c.deleted_at);
  if (filterStatus === 'featured') filtered = filtered.filter(c => c.is_featured === 1 && !c.deleted_at);
  if (filterStatus === 'deleted') filtered = filtered.filter(c => c.deleted_at);
  if (filterStatus === 'all') filtered = filtered.filter(c => !c.deleted_at);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleAll = () => {
    setSelectedIds(prev => prev.length === filtered.length ? [] : filtered.map(c => c.id));
  };

  const openAiModal = async (cat) => {
    setAiModal({ category: cat, prompt: "", loading: true, generating: false, result: null });
    try {
      const res = await axios.get(`${API}/admin/categories/${cat.id}/ai-prompt`, { withCredentials: true });
      setAiModal(prev => ({ ...prev, prompt: res.data.prompt, isPreset: res.data.is_preset, loading: false }));
    } catch {
      setAiModal(prev => ({ ...prev, prompt: "", loading: false }));
    }
  };

  const generateCategoryImage = async () => {
    if (!aiModal) return;
    setAiModal(prev => ({ ...prev, generating: true, result: null }));
    try {
      const res = await axios.post(`${API}/admin/categories/${aiModal.category.id}/generate-image`, { prompt: aiModal.prompt }, { withCredentials: true });
      setAiModal(prev => ({ ...prev, generating: false, result: res.data }));
    } catch (e) {
      setAiModal(prev => ({ ...prev, generating: false }));
      alert(e.response?.data?.detail || "Image generation failed. Try again or upload manually.");
    }
  };

  const approveCategoryImage = async () => {
    if (!aiModal?.result) return;
    try {
      await axios.post(`${API}/admin/categories/${aiModal.category.id}/approve-image`, { image_url: aiModal.result.image_url }, { withCredentials: true });
      setAiModal(null);
      fetchCategories();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to approve image");
    }
  };

  const saveCategoryPrompt = async () => {
    if (!aiModal) return;
    try {
      await axios.put(`${API}/admin/categories/${aiModal.category.id}/ai-prompt`, { prompt: aiModal.prompt }, { withCredentials: true });
      alert("Prompt saved as preset.");
    } catch {
      alert("Failed to save prompt.");
    }
  };

  const categoriesWithoutImages = categories.filter(c => !c.image_url);

  return (
    <>
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 mb-5 justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              type="text" data-testid="admin-cat-search"
              placeholder="Search categories..."
              className="pl-9 pr-4 py-2 rounded-lg border border-slate-800 bg-slate-900 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/30 w-60"
              value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <select
            value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            data-testid="admin-cat-filter"
            className="px-3 py-2 rounded-lg border border-slate-800 bg-slate-900 text-slate-300 text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="featured">Featured</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2">
              <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-800 bg-slate-900 text-sm text-slate-300">
                <option value="">Bulk Actions ({selectedIds.length})</option>
                <option value="activate">Activate</option>
                <option value="deactivate">Deactivate</option>
                <option value="feature">Mark Featured</option>
                <option value="unfeature">Remove Featured</option>
                <option value="delete">Delete</option>
                <option value="restore">Restore</option>
              </select>
              <button onClick={handleBulkAction} disabled={!bulkAction} className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-50">Apply</button>
            </div>
          )}
          <button
            onClick={() => { setEditingCat(null); setShowForm(true); }}
            data-testid="admin-add-category-btn"
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20"
          >
            <Plus size={16} /> Add Category
          </button>
          {categoriesWithoutImages.length > 0 && (
            <button onClick={() => setBulkGenModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-500 transition-all"
              data-testid="bulk-generate-btn">
              <Sparkles size={14} /> Generate Missing ({categoriesWithoutImages.length})
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="admin-categories-table">
            <thead>
              <tr className="bg-slate-950/50 border-b border-slate-800">
                <th className="px-4 py-3 w-8">
                  <input type="checkbox" className="w-3.5 h-3.5 rounded border-slate-700"
                    checked={selectedIds.length > 0 && selectedIds.length === filtered.length}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase">Image</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase">Category</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase">Slug</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase text-center">Channels</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase text-center">Status</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase text-center">Featured</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase text-center">Order</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {paginated.map((cat) => (
                <tr key={cat.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`admin-cat-row-${cat.id}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" className="w-3.5 h-3.5 rounded border-slate-700"
                      checked={selectedIds.includes(cat.id)} onChange={() => setSelectedIds(prev => prev.includes(cat.id) ? prev.filter(id => id !== cat.id) : [...prev, cat.id])}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {cat.image_url ? (
                      <img src={cat.image_url} alt="" className="w-12 h-8 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-8 rounded flex items-center justify-center bg-slate-800">
                        <ImageIcon size={12} className="text-slate-600" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ background: `${cat.accent_color || '#64748B'}22` }}>
                        <div className="w-2 h-2 rounded-full" style={{ background: cat.accent_color || '#64748B' }} />
                      </div>
                      <div>
                        <div className="font-medium text-white text-xs">{cat.name_fr || cat.name}</div>
                        <div className="text-[10px] text-slate-500">{cat.name_en || ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-[10px] text-slate-400 bg-slate-800 px-1.5 py-0.5 rounded">{cat.slug || '—'}</code>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-xs text-slate-300">{channelCounts[cat.id] || 0}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleToggle(cat)} data-testid={`toggle-${cat.id}`}>
                      {cat.is_active === 1 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-600/10 text-green-400 text-[10px] font-medium">
                          <Eye size={10} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-700/30 text-slate-500 text-[10px] font-medium">
                          <EyeOff size={10} /> Inactive
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleToggleFeatured(cat)} data-testid={`featured-${cat.id}`}>
                      {cat.is_featured === 1 ? (
                        <Star size={14} className="text-yellow-400 fill-yellow-400 mx-auto" />
                      ) : (
                        <StarOff size={14} className="text-slate-600 mx-auto" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-slate-400">{cat.display_order ?? '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {cat.deleted_at ? (
                        <button onClick={() => handleRestore(cat)} className="p-1.5 text-green-400 hover:text-green-300 transition-colors flex items-center gap-1 text-[10px] font-medium bg-green-500/10 rounded-md px-2" title="Restore" data-testid={`restore-${cat.id}`}>
                          <RotateCcw size={12} /> Restore
                        </button>
                      ) : (
                        <>
                          {!cat.image_url && (
                            <button onClick={() => openAiModal(cat)} className="p-1.5 text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1 text-[10px] font-medium bg-purple-500/10 rounded-md px-2"
                              title="Generate AI Image" data-testid={`generate-img-${cat.id}`}>
                              <Wand2 size={12} /> Generate
                            </button>
                          )}
                          {cat.image_url && (
                            <button onClick={() => openAiModal(cat)} className="p-1.5 text-slate-500 hover:text-purple-400 transition-colors" title="Regenerate Image">
                              <Wand2 size={14} />
                            </button>
                          )}
                          <button onClick={() => viewChannels(cat)} className="p-1.5 text-slate-500 hover:text-blue-400 transition-colors" title="View Channels" data-testid={`channels-${cat.id}`}>
                            <Tv size={14} />
                          </button>
                          <a href={`/categories/${cat.slug}`} target="_blank" rel="noreferrer" className="p-1.5 text-slate-500 hover:text-blue-400 transition-colors" title="Preview Page" data-testid={`preview-${cat.id}`}>
                            <ExternalLink size={14} />
                          </a>
                          <button onClick={() => { setEditingCat(cat); setShowForm(true); }} className="p-1.5 text-slate-500 hover:text-blue-400 transition-colors" title="Edit" data-testid={`edit-${cat.id}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => handleDuplicate(cat)} className="p-1.5 text-slate-500 hover:text-purple-400 transition-colors" title="Duplicate">
                            <Copy size={14} />
                          </button>
                          <button onClick={() => handleDelete(cat)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors" title="Delete" data-testid={`delete-${cat.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-slate-500 text-sm">
                    No categories found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-3 border-t border-slate-800 flex items-center justify-between bg-slate-950/50 text-xs">
            <span className="text-slate-500">
              {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
            </span>
            <div className="flex gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2.5 py-1 rounded border border-slate-800 text-slate-400 disabled:opacity-40 hover:bg-slate-800">Prev</button>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2.5 py-1 rounded border border-slate-800 text-slate-400 disabled:opacity-40 hover:bg-slate-800">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Category Form Modal */}
      {showForm && (
        <CategoryForm
          category={editingCat}
          allCategories={categories}
          onClose={() => { setShowForm(false); setEditingCat(null); }}
          onSave={() => { setShowForm(false); setEditingCat(null); fetchCategories(); }}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />

      {/* AI Image Generation Modal */}
      {aiModal && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-10 bg-slate-950/80 backdrop-blur-sm overflow-y-auto">
          <div className="bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl border border-slate-800 my-4" data-testid="ai-gen-modal">
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg text-white flex items-center gap-2"><Wand2 size={18} className="text-purple-400" /> Generate Category Image</h3>
                <p className="text-xs text-slate-500 mt-1">{aiModal.category.name_fr || aiModal.category.name}</p>
              </div>
              <button onClick={() => setAiModal(null)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Size info */}
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/5 border border-blue-500/20 rounded-lg text-[11px] text-blue-300">
                <ImageIcon size={12} className="flex-shrink-0" /> Output: 1600 × 900 px · 16:9 · Optimized for category cover
              </div>

              {/* Prompt */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-slate-400">AI Prompt {aiModal.isPreset && <span className="text-purple-400 ml-1">(Preset)</span>}</label>
                  <div className="flex gap-2">
                    <button onClick={saveCategoryPrompt} className="text-[10px] text-slate-500 hover:text-blue-400 flex items-center gap-1"><Save size={10} /> Save as Preset</button>
                    <button onClick={() => { setAiModal(prev => ({ ...prev, loading: true })); openAiModal(aiModal.category); }}
                      className="text-[10px] text-slate-500 hover:text-purple-400 flex items-center gap-1"><RotateCcw size={10} /> Restore Default</button>
                  </div>
                </div>
                {aiModal.loading ? (
                  <div className="h-32 bg-slate-800 rounded-lg animate-pulse" />
                ) : (
                  <textarea rows={6} value={aiModal.prompt || ""} onChange={e => setAiModal(prev => ({ ...prev, prompt: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white resize-none focus:border-purple-500 focus:outline-none"
                    data-testid="ai-prompt-textarea" />
                )}
              </div>

              {/* Generate Button */}
              <button onClick={generateCategoryImage} disabled={aiModal.generating || !aiModal.prompt}
                className="w-full px-4 py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="generate-category-image-btn">
                {aiModal.generating ? (
                  <><Loader2 size={16} className="animate-spin" /> Generating category image... This may take a moment.</>
                ) : (
                  <><Wand2 size={16} /> Generate Image</>
                )}
              </button>

              {/* Result */}
              {aiModal.result && (
                <div className="space-y-3">
                  <div className="rounded-xl overflow-hidden border border-slate-700">
                    <img src={aiModal.result.image_url.startsWith("/api") ? `${window.location.origin}${aiModal.result.image_url}` : aiModal.result.image_url}
                      alt="Generated" className="w-full aspect-video object-cover" data-testid="generated-image-preview" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={approveCategoryImage}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-green-600 text-white font-medium hover:bg-green-500 flex items-center justify-center gap-2"
                      data-testid="approve-image-btn">
                      <Check size={14} /> Approve & Save
                    </button>
                    <button onClick={generateCategoryImage} disabled={aiModal.generating}
                      className="px-4 py-2.5 rounded-xl bg-slate-800 text-slate-300 font-medium hover:bg-slate-700 flex items-center gap-2"
                      data-testid="regenerate-btn">
                      <RotateCcw size={14} /> Regenerate
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Generation Modal */}
      {bulkGenModal && (
        <BulkGenerateModal
          categories={categoriesWithoutImages}
          onClose={() => setBulkGenModal(false)}
          onComplete={() => { setBulkGenModal(false); fetchCategories(); }}
        />
      )}

      {/* View Channels Modal */}
      {channelsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-lg" data-testid="channels-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h3 className="text-base font-semibold text-white">Channels in {channelsModal.name_en || channelsModal.name}</h3>
              <button onClick={() => setChannelsModal(null)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"><X size={18} /></button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {channelsLoading ? (
                <div className="text-center py-8"><Loader2 size={20} className="animate-spin text-blue-400 mx-auto" /><p className="text-xs text-slate-500 mt-2">Loading channels...</p></div>
              ) : channelsData.length === 0 ? (
                <div className="text-center py-8"><Tv size={24} className="text-slate-600 mx-auto mb-2" /><p className="text-sm text-slate-500">No channels in this category</p></div>
              ) : (
                <div className="space-y-2">
                  {channelsData.map(ch => (
                    <div key={ch.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-800/50 border border-slate-700/30">
                      <img src={ch.logo_url || ""} alt="" className="w-8 h-8 rounded-lg object-contain bg-slate-700 flex-shrink-0" onError={e => { e.target.style.display = "none"; }} />
                      <div className="min-w-0 flex-grow">
                        <p className="text-sm text-white truncate">{ch.name}</p>
                        <span className="text-[10px] text-slate-500">{(ch.views || 0).toLocaleString()} views</span>
                      </div>
                      <span className={cn("text-[10px] px-2 py-0.5 rounded-full", ch.is_active ? "bg-green-500/10 text-green-400" : "bg-slate-500/10 text-slate-400")}>
                        {ch.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  ))}
                  <p className="text-center text-[10px] text-slate-600 pt-2">{channelsData.length} channels</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function BulkGenerateModal({ categories, onClose, onComplete }) {
  const [selected, setSelected] = useState(() => categories.map(c => c.id));
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  const [current, setCurrent] = useState(0);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const startGeneration = async () => {
    setGenerating(true);
    setResults([]);
    const ids = selected;
    for (let i = 0; i < ids.length; i++) {
      setCurrent(i + 1);
      try {
        const res = await axios.post(`${API}/admin/categories/${ids[i]}/generate-image`, {}, { withCredentials: true });
        if (res.data.success) {
          await axios.post(`${API}/admin/categories/${ids[i]}/approve-image`, { image_url: res.data.image_url }, { withCredentials: true });
          setResults(prev => [...prev, { id: ids[i], success: true }]);
        }
      } catch {
        setResults(prev => [...prev, { id: ids[i], success: false }]);
      }
    }
    setGenerating(false);
  };

  const done = results.length === selected.length && results.length > 0;
  const successCount = results.filter(r => r.success).length;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-10 bg-slate-950/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-slate-900 rounded-2xl w-full max-w-lg shadow-2xl border border-slate-800 my-4" data-testid="bulk-gen-modal">
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <h3 className="font-bold text-lg text-white flex items-center gap-2"><Sparkles size={18} className="text-purple-400" /> Bulk Generate Category Images</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {!generating && !done && (
            <>
              <p className="text-sm text-slate-400">{categories.length} categories without images. Select which to generate:</p>
              <div className="space-y-1">
                {categories.map(c => (
                  <label key={c.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer">
                    <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="rounded border-slate-600" />
                    <span className="text-sm text-white">{c.name_fr || c.name}</span>
                    <span className="text-[10px] text-slate-500">{c.name_en || ""}</span>
                  </label>
                ))}
              </div>
              <button onClick={startGeneration} disabled={selected.length === 0}
                className="w-full px-4 py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-500 disabled:opacity-40"
                data-testid="start-bulk-gen-btn">
                Generate {selected.length} Images
              </button>
            </>
          )}
          {generating && (
            <div className="text-center py-8 space-y-3">
              <Loader2 size={24} className="animate-spin text-purple-400 mx-auto" />
              <p className="text-sm text-white">Generating {current} of {selected.length}...</p>
              <p className="text-xs text-slate-500">This may take a few minutes. Please do not close this window.</p>
              <div className="w-full bg-slate-800 rounded-full h-2">
                <div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: `${(current / selected.length) * 100}%` }} />
              </div>
            </div>
          )}
          {done && (
            <div className="text-center py-8 space-y-3">
              <Check size={32} className="text-green-400 mx-auto" />
              <p className="text-sm text-white">{successCount} of {selected.length} images generated successfully.</p>
              <button onClick={onComplete} className="px-6 py-2.5 rounded-xl bg-green-600 text-white font-medium hover:bg-green-500">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
